import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { PartyAction, ActionType, TargetType } from '@/types'
import type { RecruitActionParameters } from '@/lib/resolvers/recruitResolver'

interface QueuedActionCardProps {
  action: PartyAction
  onCancel: (actionId: string) => void
  isCancelling?: boolean
  targetName?: string
}

const ACTION_ICONS: Record<ActionType, string> = {
  campaign: '🎯',
  fundraise: '💰',
  poll: '📊',
  recruit: '👤',
  attack: '⚔️',
  pr: '📢',
  internal: '🔧',
  organize: '🏛️',
}

const ACTION_LABELS: Record<ActionType, string> = {
  campaign: 'Campaign',
  fundraise: 'Fundraise',
  poll: 'Poll',
  recruit: 'Recruit',
  attack: 'Attack',
  pr: 'Public Relations',
  internal: 'Internal',
  organize: 'Organize',
}

const TARGET_LABELS: Record<TargetType, string> = {
  district: 'District',
  region: 'Region',
  cohort: 'Demographic',
  party: 'Party',
  candidate: 'Candidate',
  national: 'National',
}

export function QueuedActionCard({
  action,
  onCancel,
  isCancelling = false,
  targetName,
}: QueuedActionCardProps) {
  const icon = ACTION_ICONS[action.action_type] ?? '❓'
  const label = ACTION_LABELS[action.action_type] ?? action.action_type
  const targetLabel = TARGET_LABELS[action.target_type] ?? action.target_type

  const displaySubtype = action.action_subtype
    ? ` - ${formatSubtype(action.action_subtype)}`
    : ''

  // Extract candidate name for recruit actions
  const recruitDetails = getRecruitDetails(action)

  // Extract attack-specific details
  const attackDetails = getAttackDetails(action)

  return (
    <div className="border rounded-lg p-3 bg-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">{icon}</span>
            <span className="font-medium text-sm">
              {label}
              {displaySubtype}
            </span>
          </div>
          {/* Recruitment-specific details */}
          {recruitDetails && (
            <div className="text-xs text-muted-foreground mt-1 truncate">
              Recruiting: {recruitDetails.candidateName}
            </div>
          )}
          {/* Attack-specific details */}
          {attackDetails && (
            <div className="text-xs text-muted-foreground mt-1 truncate">
              Target: {attackDetails.targetPartyName}
              {attackDetails.scope === 'district' && attackDetails.districtName && (
                <span> in {attackDetails.districtName}</span>
              )}
            </div>
          )}
          {/* Standard target display for non-recruit, non-attack actions */}
          {!recruitDetails && !attackDetails && action.target_id && (
            <div className="text-xs text-muted-foreground mt-1 truncate">
              Target: {targetName ?? `${targetLabel}`}
            </div>
          )}
          {!recruitDetails && !attackDetails && !action.target_id && action.target_type === 'national' && (
            <div className="text-xs text-muted-foreground mt-1">
              National scope
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1">
            Cost: {action.action_points_spent} AP
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground hover:text-destructive shrink-0"
          onClick={() => onCancel(action.id)}
          disabled={isCancelling}
          title="Cancel action"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

/**
 * Extract recruitment-specific details from action parameters.
 */
function getRecruitDetails(action: PartyAction): { candidateName: string } | null {
  if (action.action_type !== 'recruit') return null

  const params = action.parameters as unknown as RecruitActionParameters | undefined
  if (!params?.candidate_data?.name) return null

  return {
    candidateName: params.candidate_data.name,
  }
}

/**
 * Extract attack-specific details from action parameters.
 */
function getAttackDetails(action: PartyAction): {
  targetPartyName: string
  scope?: 'national' | 'district'
  districtName?: string
} | null {
  if (action.action_type !== 'attack') return null

  const params = action.parameters as {
    target_party_name?: string
    scope?: 'national' | 'district'
    target_district_name?: string
  } | undefined

  if (!params?.target_party_name) return null

  return {
    targetPartyName: params.target_party_name,
    scope: params.scope,
    districtName: params.target_district_name,
  }
}

function formatSubtype(subtype: string): string {
  // Convert snake_case or kebab-case to Title Case
  return subtype
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
